import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import Badge from '../../components/ui/Badge';
import {
  Button,
  GeneralList,
  GeneralListSortableTh,
  PrinterIcon,
  RankSelect,
  RefreshIcon,
  SearchIcon,
  UserIcon,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../components/ui';
import { formatDate } from '../../config/appConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { usePersistedListFilters } from '../../hooks/usePersistedListFilters';
import { getListStateStorageKey } from '../../lib/listStatePersistence';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import { CrewReportViewAction, useCrewReportOpen } from './reportShared';
import { reliefBoardPrintPath } from './reports/reliefBoardPrintMeta';
import type { ReliefBoardResponse, ReliefBoardRow } from './types';

const ROUTE = '/crew/relief-board';
const STORAGE_KEY = getListStateStorageKey(ROUTE);

type NrTab = 'all' | 'nr';

type Filters = {
  tab: NrTab;
  rankId: string;
  search: string;
};

const display = (value: string | number | null | undefined) =>
  value == null || value === '' ? '—' : String(value);

const displayDate = (value: string | null | undefined) => (value ? formatDate(value) : '—');

function sortValue(row: ReliefBoardRow, key: string): string | number {
  switch (key) {
    case 'name':
      return (row.name || '').toLowerCase();
    case 'rank':
      return (row.rank || '').toLowerCase();
    case 'nationality':
      return (row.nationality || '').toLowerCase();
    case 'age':
      return row.age ?? -1;
    case 'availableFrom':
      return row.availableFrom || '';
    case 'availableTo':
      return row.availableTo || '';
    case 'availabilityStatus':
      return (row.availabilityStatus || '').toLowerCase();
    case 'daysAshore':
      return row.daysAshore ?? -1;
    case 'lastSignOffDate':
      return row.lastSignOffDate || '';
    case 'lastSignOffReason':
      return (row.lastSignOffReason || '').toLowerCase();
    case 'lastVessel':
      return (row.lastVessel || '').toLowerCase();
    case 'lastRank':
      return (row.lastRank || '').toLowerCase();
    case 'monthsLastTour':
      return row.monthsLastTour ?? -1;
    case 'monthsInRank':
      return row.monthsInRank ?? -1;
    case 'lastEngine':
      return (row.lastEngine || '').toLowerCase();
    case 'lastDwt':
      return row.lastDwt ?? -1;
    case 'vesselTypes':
      return (row.vesselTypes || '').toLowerCase();
    case 'lastWage':
      return (row.lastWage || '').toLowerCase();
    case 'lastAppraisalDate':
      return row.lastAppraisalDate || '';
    case 'overallEvaluation':
      return (row.overallEvaluation || '').toLowerCase();
    case 'toBePromoted':
      return row.toBePromoted ? 1 : 0;
    case 'medicalStatus':
      return (row.medicalStatus || '').toLowerCase();
    case 'medicalNextDue':
      return row.medicalNextDue || '';
    case 'expiredCerts':
      return row.expiredCerts;
    case 'notRecommended':
      return row.notRecommended ? 1 : 0;
    case 'manningAgent':
      return (row.manningAgent || '').toLowerCase();
    case 'nearestAirport':
      return (row.nearestAirport || '').toLowerCase();
    case 'nearestPort':
      return (row.nearestPort || '').toLowerCase();
    case 'mobile':
      return (row.mobile || '').toLowerCase();
    case 'lastContactedDate':
      return row.lastContactedDate || '';
    case 'availabilityNotes':
      return (row.availabilityNotes || '').toLowerCase();
    default:
      return row.crewId;
  }
}

const DATE_SORT_KEYS = new Set([
  'availableFrom',
  'availableTo',
  'lastSignOffDate',
  'lastAppraisalDate',
  'medicalNextDue',
  'lastContactedDate',
  'daysAshore',
  'expiredCerts',
  'age',
  'monthsLastTour',
  'monthsInRank',
  'lastDwt',
]);

const ReliefBoardPage: React.FC = () => {
  const { colors } = useTheme();
  const { listReturnPath, openCrew } = useCrewReportOpen();

  const [persisted, setPersisted] = usePersistedListFilters<Filters>(STORAGE_KEY);
  const [tab, setTab] = useState<NrTab>(persisted?.tab ?? 'all');
  const [rankId, setRankId] = useState(persisted?.rankId ?? '');
  const [searchInput, setSearchInput] = useState(persisted?.search ?? '');
  const [search, setSearch] = useState(persisted?.search ?? '');
  const [page, setPage] = useState(1);
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const { sortConfig, handleSort } = useGeneralListSort(
    { key: 'name', direction: 'asc' },
    {
      defaultDirectionForKey: (key) => (DATE_SORT_KEYS.has(key) ? 'desc' : 'asc'),
    },
  );

  useEffect(() => {
    setPersisted({ tab, rankId, search });
  }, [tab, rankId, search, setPersisted]);

  useEffect(() => {
    setPage(1);
  }, [tab, rankId, search, itemsPerPage, sortConfig.key, sortConfig.direction]);

  const listUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (rankId) params.set('rankId', rankId);
    const qs = params.toString();
    return `/crew/reports/relief-board${qs ? `?${qs}` : ''}`;
  }, [rankId]);

  const { data, error, isLoading, mutate } = useSWR<ReliefBoardResponse>(listUrl, fetcher, {
    revalidateOnFocus: false,
  });

  const handleSearch = useCallback(() => {
    setSearch(searchInput.trim());
    setPage(1);
  }, [searchInput]);

  const filteredRows = useMemo(() => {
    let list = data?.rows ?? [];
    if (tab === 'nr') list = list.filter((r) => r.notRecommended);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          (r.crewRef && r.crewRef.toLowerCase().includes(q)) ||
          (r.rank && r.rank.toLowerCase().includes(q)) ||
          (r.lastVessel && r.lastVessel.toLowerCase().includes(q)),
      );
    }
    return list;
  }, [data?.rows, tab, search]);

  const sortedRows = useMemo(() => {
    const rows = [...filteredRows];
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const av = sortValue(a, sortConfig.key);
      const bv = sortValue(b, sortConfig.key);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return (a.crewId - b.crewId) * dir;
    });
    return rows;
  }, [filteredRows, sortConfig.key, sortConfig.direction]);

  const total = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));
  const pageRows = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return sortedRows.slice(start, start + itemsPerPage);
  }, [sortedRows, page, itemsPerPage]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const hasFilters = Boolean(search.trim() || rankId || tab !== 'all');

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setTab('all');
    setRankId('');
  };

  const medicalBadge = (value: string | null) => {
    if (!value) return <span className={colors.textMuted}>—</span>;
    const upper = String(value).toUpperCase();
    if (upper === 'FIT') {
      return (
        <Badge variant="success" className="rounded-md px-2 py-0.5">
          FIT
        </Badge>
      );
    }
    if (upper === 'UNFIT' || upper === 'TEMPORARY_UNFIT') {
      return (
        <Badge variant="danger" className="rounded-md px-2 py-0.5">
          {upper === 'TEMPORARY_UNFIT' ? 'TEMP UNFIT' : 'UNFIT'}
        </Badge>
      );
    }
    return <span className={cn('whitespace-nowrap', colors.text)}>{value}</span>;
  };

  return (
    <GeneralList
      tabs={[
        { id: 'all', label: 'All' },
        { id: 'nr', label: 'NR only' },
      ]}
      activeTab={tab}
      onTabChange={(value) => setTab(value as NrTab)}
      sorting={{ sortConfig, onSort: handleSort }}
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: 'Name, ref, rank…',
        title: 'Searches name, crew ref, rank, and last vessel',
        onSubmit: handleSearch,
      }}
      filtersInline
      filters={
        <div className="w-[11rem] max-w-full">
          <RankSelect
            value={rankId}
            onChange={(e) => setRankId(String(e.target.value))}
            placeholder="Rank"
            searchPlaceholder="Search ranks…"
            includeIds={rankId ? [rankId] : undefined}
            triggerClassName="!h-10 !min-h-10 !py-0 box-border"
          />
        </div>
      }
      hasFilters={hasFilters}
      onClearFilters={clearFilters}
      actions={
        <div className="flex h-10 items-center gap-2">
          <Button type="button" className="h-10" disabled={isLoading} onClick={handleSearch}>
            <SearchIcon className="h-4 w-4" />
            Search
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-10"
            disabled={isLoading}
            onClick={() => void mutate()}
            title="Refresh"
          >
            <RefreshIcon className="h-4 w-4" />
            Refresh
          </Button>
          <Link
            to={reliefBoardPrintPath({ tab, rankId, search })}
            title="Print preview"
            className={cn(
              'inline-flex h-10 items-center gap-2 rounded-md border px-3 text-sm font-medium transition-colors',
              colors.border,
              colors.text,
              'hover:bg-black/5 dark:hover:bg-white/10',
            )}
          >
            <PrinterIcon className="h-4 w-4" />
            Print
          </Link>
        </div>
      }
      isLoading={isLoading}
      loadingLabel="Loading relief board…"
      isEmpty={!error && pageRows.length === 0}
      emptyState={{
        icon: <UserIcon className="h-12 w-12" />,
        title: hasFilters ? 'No matching crew' : 'No shore crew on the board',
        hint: hasFilters
          ? 'Try adjusting filters or clearing the search.'
          : 'Crew available for placement will show up here.',
        action: hasFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium',
              colors.border,
              colors.text,
            )}
          >
            Clear filters
          </button>
        ) : undefined,
      }}
      banner={
        error ? (
          <p className={cn('rounded-xl border px-3 py-2 text-sm', colors.error)}>
            Could not load relief board.
          </p>
        ) : null
      }
      pagination={{
        page,
        totalPages,
        total,
        itemsPerPage,
        pageSizeOptions,
        onPageChange: setPage,
        onItemsPerPageChange: (size) => {
          setItemsPerPage(size);
          setPage(1);
        },
      }}
    >
      <table className="w-full min-w-[2200px] text-sm">
        <thead>
          <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
            <th className={cn('sticky top-0 z-10 w-12 font-medium', colors.surface)} />
            <GeneralListSortableTh sortKey="name" className="whitespace-nowrap">
              Crew
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="rank" className="whitespace-nowrap">
              Rank
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="nationality" className="whitespace-nowrap">
              Nat.
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="age" className="whitespace-nowrap" align="right">
              Age
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="availableFrom" className="whitespace-nowrap">
              Av. from
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="availableTo" className="whitespace-nowrap">
              Av. to
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="availabilityStatus" className="whitespace-nowrap">
              Av. status
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="daysAshore" className="whitespace-nowrap" align="right">
              Days ash.
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="lastSignOffDate" className="whitespace-nowrap">
              S/Off
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="lastSignOffReason" className="whitespace-nowrap">
              S/Off reason
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="lastVessel" className="whitespace-nowrap">
              Last vessel
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="lastRank" className="whitespace-nowrap">
              Last rank
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="monthsLastTour" className="whitespace-nowrap" align="right">
              M. last
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="monthsInRank" className="whitespace-nowrap" align="right">
              M. rank
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="lastEngine" className="whitespace-nowrap">
              Last eng.
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="lastDwt" className="whitespace-nowrap" align="right">
              Last DWT
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="vesselTypes" className="whitespace-nowrap">
              Vsl types
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="lastWage" className="whitespace-nowrap">
              Last wage
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="lastAppraisalDate" className="whitespace-nowrap">
              Appraisal
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="overallEvaluation" className="whitespace-nowrap">
              App. eval
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="toBePromoted" className="whitespace-nowrap">
              Promote
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="medicalStatus" className="whitespace-nowrap">
              Med. status
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="medicalNextDue" className="whitespace-nowrap">
              Med. due
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="expiredCerts" className="whitespace-nowrap" align="right">
              Certs exp.
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="notRecommended" className="whitespace-nowrap">
              NR
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="manningAgent" className="whitespace-nowrap">
              Agent
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="nearestAirport" className="whitespace-nowrap">
              Airport
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="nearestPort" className="whitespace-nowrap">
              Port
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="mobile" className="whitespace-nowrap">
              Mobile
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="lastContactedDate" className="whitespace-nowrap">
              Contacted
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="availabilityNotes" className="whitespace-nowrap">
              Notes
            </GeneralListSortableTh>
          </tr>
        </thead>
        <tbody>
          {pageRows.map((row) => (
            <tr
              key={row.crewId}
              onClick={() => openCrew(row.crewId)}
              className={cn(
                'group cursor-pointer border-b transition last:border-b-0',
                colors.border,
                colors.interactiveHover,
              )}
            >
              <td onClick={(e) => e.stopPropagation()}>
                <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />
              </td>
              <td>
                <Link
                  to={`/crew/${row.crewId}`}
                  state={{ from: listReturnPath }}
                  className={cn('text-sm font-medium hover:underline', colors.primaryFg)}
                  onClick={(e) => e.stopPropagation()}
                >
                  {row.name}
                  {row.crewRef ? (
                    <span className={cn('ml-1 text-xs font-normal', colors.textMuted)}>({row.crewRef})</span>
                  ) : null}
                </Link>
              </td>
              <td className={cn('whitespace-nowrap', colors.text)}>{display(row.rank)}</td>
              <td className={cn('whitespace-nowrap', colors.text)}>{display(row.nationality)}</td>
              <td className={cn('whitespace-nowrap text-right tabular-nums', colors.text)}>
                {display(row.age)}
              </td>
              <td className={cn('whitespace-nowrap', colors.textMuted)}>{displayDate(row.availableFrom)}</td>
              <td className={cn('whitespace-nowrap', colors.textMuted)}>{displayDate(row.availableTo)}</td>
              <td className={cn('whitespace-nowrap', colors.text)}>{display(row.availabilityStatus)}</td>
              <td className={cn('whitespace-nowrap text-right tabular-nums', colors.text)}>
                {display(row.daysAshore)}
              </td>
              <td className={cn('whitespace-nowrap', colors.textMuted)}>{displayDate(row.lastSignOffDate)}</td>
              <td className={cn('whitespace-nowrap', colors.text)}>{display(row.lastSignOffReason)}</td>
              <td className={cn('whitespace-nowrap', colors.text)}>{display(row.lastVessel)}</td>
              <td className={cn('whitespace-nowrap', colors.text)}>{display(row.lastRank)}</td>
              <td className={cn('whitespace-nowrap text-right tabular-nums', colors.text)}>
                {display(row.monthsLastTour)}
              </td>
              <td className={cn('whitespace-nowrap text-right tabular-nums', colors.text)}>
                {display(row.monthsInRank)}
              </td>
              <td className={cn('whitespace-nowrap', colors.text)}>{display(row.lastEngine)}</td>
              <td className={cn('whitespace-nowrap text-right tabular-nums', colors.text)}>
                {display(row.lastDwt)}
              </td>
              <td className={cn('whitespace-nowrap', colors.text)}>{display(row.vesselTypes)}</td>
              <td className={cn('whitespace-nowrap', colors.text)}>{display(row.lastWage)}</td>
              <td className={cn('whitespace-nowrap', colors.textMuted)}>{displayDate(row.lastAppraisalDate)}</td>
              <td className={cn('whitespace-nowrap', colors.text)}>{display(row.overallEvaluation)}</td>
              <td>
                {row.toBePromoted ? (
                  <Badge variant="info" className="rounded-md px-2 py-0.5">
                    Yes
                  </Badge>
                ) : (
                  <span className={colors.textMuted}>—</span>
                )}
              </td>
              <td>{medicalBadge(row.medicalStatus)}</td>
              <td className={cn('whitespace-nowrap', colors.textMuted)}>{displayDate(row.medicalNextDue)}</td>
              <td className="text-right">
                {row.expiredCerts > 0 ? (
                  <Badge variant="danger" className="rounded-md px-2 py-0.5">
                    {String(row.expiredCerts)}
                  </Badge>
                ) : (
                  <span className={cn('whitespace-nowrap', colors.textMuted)}>
                    {row.nextCertExpiry ? formatDate(row.nextCertExpiry) : '—'}
                  </span>
                )}
              </td>
              <td>
                {row.notRecommended ? (
                  <Badge variant="danger" className="rounded-md px-2 py-0.5">
                    NR
                  </Badge>
                ) : (
                  <span className={colors.textMuted}>—</span>
                )}
              </td>
              <td className={cn('whitespace-nowrap', colors.text)}>{display(row.manningAgent)}</td>
              <td className={cn('whitespace-nowrap', colors.text)}>{display(row.nearestAirport)}</td>
              <td className={cn('whitespace-nowrap', colors.text)}>{display(row.nearestPort)}</td>
              <td className={cn('whitespace-nowrap', colors.text)}>{display(row.mobile)}</td>
              <td className={cn('whitespace-nowrap', colors.textMuted)}>{displayDate(row.lastContactedDate)}</td>
              <td className={cn('max-w-[14rem] truncate', colors.textMuted)} title={row.availabilityNotes || undefined}>
                {display(row.availabilityNotes)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </GeneralList>
  );
};

export default ReliefBoardPage;
